import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useContacts } from "@/hooks/use-api";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDate } from "@/lib/utils";
import { RoleBadge, HostLink } from "./shared";

type Row = Record<string, unknown>;

const columns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "last_name",
    header: "Name",
    cell: ({ row }) => {
      const first = row.original.first_name as string | undefined;
      const last = row.original.last_name as string | undefined;
      const fullName = [first, last].filter(Boolean).join(" ") || "—";
      const company = row.original.company_name as string | undefined;
      return (
        <div>
          <p className="font-medium transition-colors group-hover:text-[color:var(--color-accent)]">
            {fullName}
          </p>
          {company && (
            <p className="typo-label text-[color:var(--color-text-lo)] normal-case tracking-normal">
              {company}
            </p>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "email",
    header: "Email",
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-md)]">
        {(row.original.email as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "phone",
    header: "Phone",
    enableSorting: false,
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-md)]">
        {(row.original.phone as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "role_type",
    header: "Role",
    enableSorting: false,
    cell: ({ row }) => <RoleBadge role={row.original.role_type as string} />,
  },
  {
    accessorKey: "business_name",
    header: "Business",
    enableSorting: false,
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-md)]">
        {(row.original.business_name as string) || "—"}
      </span>
    ),
  },
  {
    id: "host",
    header: "Linked Record",
    enableSorting: false,
    cell: ({ row }) => (
      <HostLink
        roleType={row.original.role_type as string}
        contactId={row.original.contact_id as string}
      />
    ),
  },
  {
    accessorKey: "role_created_at",
    header: "Created",
    enableSorting: false,
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-md)]">
        {formatDate(row.original.role_created_at as string)}
      </span>
    ),
  },
];

const ROLE_FILTERS = [
  { value: "all", label: "All roles" },
  { value: "customer", label: "Customers" },
  { value: "team_member", label: "Team Members" },
  { value: "trade_partner", label: "Trade Partners" },
];

export default function V2ContactsPage() {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([
    { id: "last_name", desc: false },
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const [roleType, setRoleType] = useState("all");
  const pageSize = 50;

  const { data, isLoading } = useContacts({
    offset: pageIndex * pageSize,
    limit: pageSize,
    role_type: roleType,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
  });

  const contacts = (data?.contacts ?? []) as Row[];
  const total = data?.total ?? 0;

  const roleFilter = (
    <Select
      value={roleType}
      onValueChange={(value) => {
        setRoleType(value);
        setPageIndex(0);
      }}
    >
      <SelectTrigger className="w-[180px]" aria-label="Filter by role">
        <SelectValue placeholder="All roles" />
      </SelectTrigger>
      <SelectContent>
        {ROLE_FILTERS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <ListPage
      title="Contacts"
      subtitle={`${total.toLocaleString()} total roles across contacts`}
      filters={roleFilter}
    >
      <DataTable
        columns={columns}
        data={contacts}
        total={total}
        isLoading={isLoading}
        onRowClick={(row) => navigate(`/contacts/${row.contact_id as string}`)}
        emptyMessage="No contacts yet"
        emptyDescription="Contacts appear here once customers, team members, or vendors are added. Each row is one role a contact holds in this business."
        pageSize={pageSize}
        pageIndex={pageIndex}
        onPaginationChange={setPageIndex}
        sorting={sorting}
        onSortingChange={setSorting}
      />
    </ListPage>
  );
}
